/**
 * TEMPORARY — delete once the `$unset` migration has run in every environment.
 *
 * Fields removed from the Device schema still exist on stored documents. Mongoose keeps unknown
 * keys in `_doc`, so they would otherwise surface via `.lean()` results and `toJSON()`. The
 * pre-query hook below adds an exclusion projection to device reads so the values never leave
 * MongoDB during the migration window.
 *
 * Known gap: `.aggregate()` bypasses query middleware, so aggregation read paths still see
 * the legacy fields. That is accepted for the window — the `$unset` is the real fix.
 *
 * Removal: delete this file, and the import and `@pre` decorator that reference it in device.ts.
 */

/**
 * Dot-notation paths to exclude. MongoDB exclusion projections descend into arrays, so
 * `connectedSensors.lastSMSAlert` covers every element without positional syntax, and naming a
 * parent excludes the whole subtree.
 */
const LEGACY_DEVICE_PATHS = [
    // Removed in PR #1284
    "overWaterProtectionSettings",
    "sproutNetworkInfo.crashesPerDay",
    "sproutNetworkInfo.resetsPerDay",
    "sproutNetworkInfo.uplinksPerHour",
    "sproutNetworkInfo.downlinksPerHour",
    "sproutNetworkInfo.errorRate",
    "sproutNetworkInfo.valveStatePercentError",
    "sproutNetworkInfo.idealStandardUplinkPeriod",
    "sproutNetworkInfo.idealActiveUplinkPeriod",
    "gen4NetworkInfo.idealStandardUplinkPeriod",
    "gen4NetworkInfo.ttiApplicationId",
    "connectedSensors.lastSMSAlert",
    // Removed in PR #1299
    "sproutNetworkInfo.version",
    // Sapling teardown — whole subtrees
    "saplingNetworkInfo",
    "SaplingIrrigationVerification",
    // Removed in PR #1489 — each is duplicated by the field readers already use:
    // sproutNetworkInfo.batteryVoltage carries batteryIndicator's value, and node_id is always
    // byte-identical to EnclosureID on first-party devices
    "batteryIndicator",
    "EnclosureID",
    // Removed in PR #1493 — write-only copies of Gen4InternalStateTracker, which is what readers use
    "gen4NetworkInfo.crashCount",
    "gen4NetworkInfo.resetCount",
    "gen4NetworkInfo.lastErrorCode",
    // Removed in PR #1528 — the only value Gen4 read out of the per-port history is now carried by
    // gen4NetworkInfo.lastSensorUplinkDate. Sprout keeps its own lastContactByPort.
    "gen4NetworkInfo.lastContactByPort",
] as const;

/**
 * MongoDB rejects mixing inclusion and exclusion in one projection (`_id` is the sole
 * exception), so exclusions can only be appended to queries that aren't already
 * inclusion-projected. Skipping those is also correct: an inclusion projection returns only
 * the fields it lists, and none of the call sites list a removed field.
 */
function hasInclusionProjection(projection: Record<string, unknown> | null | undefined): boolean {
    if (!projection) return false;
    return Object.entries(projection).some(([field, value]) => field !== "_id" && (value === 1 || value === true));
}

/**
 * A projection may not contain both a path and a prefix of that path — MongoDB rejects it with
 * "Path collision" (Location31249). A caller excluding a whole parent (RefreshSensingPeriods does
 * `sproutNetworkInfo: 0`) therefore collides with any child of it in the list below, so paths that
 * the caller has already covered are dropped instead of merged.
 */
function collidesWithCallerPath(legacyPath: string, callerPaths: string[]): boolean {
    return callerPaths.some(
        (callerPath) =>
            callerPath === legacyPath ||
            legacyPath.startsWith(`${callerPath}.`) ||
            callerPath.startsWith(`${legacyPath}.`),
    );
}

/**
 * Query middleware must be attached before the model is compiled, so this is wired up with a
 * `@pre` decorator on the Device class rather than through the backend's registerHooks entry
 * point — hooks added to an already-compiled model never fire.
 */
export function excludeLegacyDeviceFields(this: {
    projection: () => Record<string, unknown> | null | undefined;
    select: (arg: Record<string, number>) => unknown;
}): void {
    const projection = this.projection();
    if (hasInclusionProjection(projection)) return;

    const callerPaths = Object.keys(projection ?? {});
    const exclusion = Object.fromEntries(
        LEGACY_DEVICE_PATHS.filter((path) => !collidesWithCallerPath(path, callerPaths)).map((path) => [path, 0]),
    );
    if (Object.keys(exclusion).length === 0) return;
    this.select(exclusion);
}
